import { CreditCard, Loader2, X } from 'lucide-react'
import { cn } from '@lib/utils/helpers'
import { getUserManagementEditUrl } from '@/lib/utils/userManagementNavigation'
import type { UsePaymentsTabReturn } from '@/features/company/types'
import {
    FILE_TYPES,
    PAY_ADDR_INVALID_RING,
    normalizePaymentFiles,
} from '@/features/company/utils/paymentsTabLogic'
import { formatPaymentUpdatedDate } from '@/features/company/utils/paymentDateFormat'
import { PAY_FIELD, PAY_BTN_PRIMARY, PAY_LINK } from './paymentsTabStyles'
import { PaymentAddrFieldError } from './PaymentAddrFieldError'
import { AchPaymentFormHelperTip } from './AchPaymentFormHelperTip'
import { PaymentFileUploadField } from './PaymentFileUploadField'
import { SectionCard } from '@/components/ui/SectionCard'

export function PaymentsTabCompanyForm({
        company,
        companyForm,
        states,
        companyPaymentInfoFieldsDisabled,
        usePaymentForAllToggleDisplay,
        switchingUsePaymentForAllProperties,
        switchingPaymentForAllTarget,
        canAchFormStatus,
        uploading,
        downloading,
        filePickerHint,
        setFilePickerHint,
        paymentAddrValidateErrors,
        updateCompanyForm,
        clearPaymentAddrFieldError,
        handleDownloadAchForm,
        handleDownloadFile,
        handleUploadFile,
        handleValidatePaymentAddress,
        handleCompanyPaymentFormSubmit,
        setShowDeleteFileConfirm,
        companyStaff,
        requestAllPropertiesToggle,
}: UsePaymentsTabReturn) {
    return (
            <form
                data-testid="company-payments-tab-form"
                data-tab-id="payments"
                className="contents"
                onSubmit={handleCompanyPaymentFormSubmit}
                noValidate
            >
            <SectionCard
                icon={CreditCard}
                title={
                    companyForm.payment_method === "ACH"
                        ? "Company payment preference"
                        : "Company payment info"
                }
                testId="company-payments-company-payment-section"
            >
                <div className="flex flex-wrap items-end gap-x-8 gap-y-4">
                    <div className="flex flex-col gap-1.5">
                        <label className="text-xs font-semibold uppercase tracking-wide text-slate-500">Method</label>
                        <select
                            data-testid="company-payments-company-method-select"
                            value={companyForm.payment_method}
                            onChange={(e) => updateCompanyForm({ payment_method: e.target.value })}
                            disabled={companyPaymentInfoFieldsDisabled}
                            className={cn(PAY_FIELD, "w-40")}
                        >
                            <option value="">Select Type</option>
                            <option value="ACH">ACH</option>
                            <option value="PAPER_CHECK">Paper</option>
                        </select>
                    </div>
                    <div className="flex flex-col gap-1.5">
                        <label className="text-xs font-semibold uppercase tracking-wide text-slate-500">Verified</label>
                        <select
                            data-testid="company-payments-company-verified-select"
                            value={companyForm.verify}
                            onChange={(e) => updateCompanyForm({ verify: e.target.value })}
                            disabled={companyPaymentInfoFieldsDisabled}
                            className={cn(PAY_FIELD, "w-32")}
                        >
                            <option value="">Select Type</option>
                            <option value="1">Yes</option>
                            <option value="0">No</option>
                        </select>
                    </div>
                    <p className="pb-2 text-sm text-slate-500">[Note: Additional processing fees may apply.]</p>
                </div>
                <div className="mt-4 flex flex-col gap-3 rounded-xl border border-slate-100 bg-slate-50/80 px-3 py-3 sm:flex-row sm:flex-wrap sm:items-center sm:gap-4">
                    <span className="text-sm font-medium text-slate-800">
                        Use this payment method for all property payments?
                    </span>
                    <div
                        className="inline-flex shrink-0 flex-wrap items-center gap-2.5"
                        role="group"
                        aria-label="Use this payment method for all property payments"
                        aria-busy={switchingUsePaymentForAllProperties}
                    >
                        <button
                            type="button"
                            data-testid="company-payments-company-use-method-for-all-properties-yes-button"
                            disabled={companyPaymentInfoFieldsDisabled}
                            onClick={() => requestAllPropertiesToggle('1')}
                            className={cn(
                                "inline-flex min-w-[4.5rem] items-center justify-center gap-2 rounded-lg border px-4 py-2 text-sm font-medium shadow-sm transition",
                                usePaymentForAllToggleDisplay === "1"
                                    ? "border-[rgb(21_151_255)] bg-[rgb(21_151_255)] text-white"
                                    : "border-slate-300 bg-white text-slate-700 hover:bg-slate-50",
                                companyPaymentInfoFieldsDisabled && "cursor-not-allowed opacity-60",
                            )}
                        >
                            {switchingUsePaymentForAllProperties && switchingPaymentForAllTarget === "1" ? (
                                <Loader2 className="h-4 w-4 shrink-0 animate-spin" aria-hidden />
                            ) : null}
                            Yes
                        </button>
                        <button
                            type="button"
                            data-testid="company-payments-company-use-method-for-all-properties-no-button"
                            disabled={companyPaymentInfoFieldsDisabled}
                            onClick={() => requestAllPropertiesToggle('0')}
                            className={cn(
                                "inline-flex min-w-[4.5rem] items-center justify-center gap-2 rounded-lg border px-4 py-2 text-sm font-medium shadow-sm transition",
                                usePaymentForAllToggleDisplay === "0"
                                    ? "border-[rgb(21_151_255)] bg-[rgb(21_151_255)] text-white"
                                    : "border-slate-300 bg-white text-slate-700 hover:bg-slate-50",
                                companyPaymentInfoFieldsDisabled && "cursor-not-allowed opacity-60",
                            )}
                        >
                            {switchingUsePaymentForAllProperties && switchingPaymentForAllTarget === "0" ? (
                                <Loader2 className="h-4 w-4 shrink-0 animate-spin" aria-hidden />
                            ) : null}
                            No
                        </button>
                    </div>
                </div>

            {/* ACH fields */}
            {companyForm.payment_method === "ACH" && (
                <div className="space-y-5 border-t border-slate-100 pt-5">
                    <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
                        <div>
                            <label className="mb-1 block text-sm font-medium text-slate-600">
                                ACH Routing<span className="font-semibold text-[#dc2626]">*</span>
                            </label>
                            <input
                                type="text"
                                data-testid="company-payments-company-ach-routing-input"
                                maxLength={10}
                                placeholder="Routing Number"
                                value={companyForm.routing_number}
                                onChange={(e) => updateCompanyForm({ routing_number: e.target.value })}
                                disabled={companyPaymentInfoFieldsDisabled}
                                className={cn(PAY_FIELD, "w-full max-w-[220px]")}
                            />
                        </div>
                        <div>
                            <label className="mb-1 block text-sm font-medium text-slate-600">
                                ACH Account<span className="font-semibold text-[#dc2626]">*</span>
                            </label>
                            <input
                                type="text"
                                data-testid="company-payments-company-ach-account-input"
                                maxLength={13}
                                placeholder="Account Number"
                                value={companyForm.account_number}
                                onChange={(e) => updateCompanyForm({ account_number: e.target.value })}
                                disabled={companyPaymentInfoFieldsDisabled}
                                className={cn(PAY_FIELD, "w-full max-w-[220px]")}
                            />
                        </div>
                        <div>
                            <label className="mb-1 block text-sm font-medium text-slate-600">
                                Account Type<span className="font-semibold text-[#dc2626]">*</span>
                            </label>
                            <select
                                data-testid="company-payments-company-ach-account-type-select"
                                value={companyForm.property_method}
                                onChange={(e) => updateCompanyForm({ property_method: e.target.value })}
                                disabled={companyPaymentInfoFieldsDisabled}
                                className={cn(PAY_FIELD, "w-full max-w-[220px]")}
                            >
                                <option value="">Select Type</option>
                                <option value="Checkings">Checking</option>
                                <option value="Savings">Savings</option>
                            </select>
                        </div>
                    </div>

                    <div className="border-t border-slate-100 pt-8">
                        <p className="mb-4 text-xs font-semibold uppercase tracking-wider text-slate-500">
                            Supporting documents
                        </p>
                        <div className="grid grid-cols-1 items-start gap-4 lg:grid-cols-2">
                            <div className="flex min-h-[200px] flex-col gap-3 rounded-xl border border-slate-200/90 bg-gradient-to-b from-slate-50/80 to-white px-3 py-4 shadow-sm sm:px-4">
                                <div className="flex flex-wrap items-start justify-between gap-2 border-b border-slate-200/80 pb-3">
                                    <div className="flex min-w-0 flex-wrap items-center gap-1">
                                        <span className="text-sm font-semibold text-slate-900">
                                            ACH Payment Form<span className="font-semibold text-[#dc2626]">*</span>
                                        </span>
                                        <AchPaymentFormHelperTip infoButtonDataTestId="company-payments-company-ach-form-helper-info-button" />
                                    </div>
                                    <button
                                        type="button"
                                        data-testid="company-payments-company-download-ach-form-button"
                                        onClick={handleDownloadAchForm}
                                        disabled={companyPaymentInfoFieldsDisabled}
                                        className={cn(PAY_LINK, "shrink-0")}
                                    >
                                        Download ACH Form
                                    </button>
                                </div>
                                {canAchFormStatus && (
                                    <div>
                                        <label className="mb-1 block text-xs font-medium text-slate-500">Form status</label>
                                        <select
                                            data-testid="company-payments-company-ach-form-status-select"
                                            value={companyForm.company_status}
                                            onChange={(e) => updateCompanyForm({ company_status: e.target.value })}
                                            disabled={companyPaymentInfoFieldsDisabled}
                                            className={cn(PAY_FIELD, "w-full max-w-xs")}
                                        >
                                            <option value="">Select Status</option>
                                            <option value="Pending">Pending</option>
                                            <option value="Completed">Completed</option>
                                        </select>
                                    </div>
                                )}
                                <div>
                                    <label className="mb-1 block text-xs font-medium text-slate-500">Upload completed form</label>
                                    <PaymentFileUploadField
                                        className="mt-1"
                                        testIdPrefix="company-payments-company-ach-form-upload"
                                        aria-label="Upload ACH payment form"
                                        disabled={companyPaymentInfoFieldsDisabled}
                                        isUploading={uploading === "company_ach_file"}
                                        chosenDisplayName={filePickerHint["company_ach_file"] ?? ""}
                                        onFilesSelected={(files) => {
                                            const key = "company_ach_file"
                                            const f = files?.[0]
                                            if (f) {
                                                setFilePickerHint((prev) => ({ ...prev, [key]: f.name }))
                                            }
                                            handleUploadFile("ach_file", files, undefined)
                                        }}
                                    />
                                    <p className="mt-2 text-xs text-slate-500">File uploads when you select it or click Upload.</p>
                                </div>
                                <div className="mt-auto space-y-2 border-t border-slate-200/80 pt-3">
                                    {normalizePaymentFiles(company?.["ach_file"]).map((f, i) => (
                                        <div key={f?.id ?? i} className="flex items-center gap-2">
                                            <button
                                                type="button"
                                                data-testid={`company-payments-company-ach-file-${String(f.id)}-download-button`}
                                                className={cn(PAY_LINK, "truncate text-left")}
                                                onClick={() => handleDownloadFile(String(f.id), FILE_TYPES.ACH)}
                                                disabled={downloading === String(f.id)}
                                            >
                                                {downloading === String(f.id) ? "…" : f?.attachment_name ?? "Download"}
                                            </button>
                                            {!companyPaymentInfoFieldsDisabled && (
                                                <button
                                                    type="button"
                                                    data-testid={`company-payments-company-ach-file-${String(f.id)}-remove-button`}
                                                    onClick={() =>
                                                        setShowDeleteFileConfirm({ fileId: String(f.id), type: "ach_file", index: i })
                                                    }
                                                    className="shrink-0 rounded p-1 text-red-600 hover:bg-red-50"
                                                    aria-label="Remove file"
                                                >
                                                    <X size={14} />
                                                </button>
                                            )}
                                        </div>
                                    ))}
                                </div>
                            </div>

                            <div className="flex min-h-[200px] flex-col gap-3 rounded-xl border border-slate-200/90 bg-gradient-to-b from-slate-50/80 to-white px-3 py-4 shadow-sm sm:px-4">
                                <div className="border-b border-slate-200/80 pb-3">
                                    <span className="text-sm font-semibold text-slate-900">
                                        Cancelled Check<span className="font-semibold text-[#dc2626]">*</span>
                                    </span>
                                </div>
                                <div className="flex-1">
                                    <label className="mb-1 block text-xs font-medium text-slate-500">Upload image or PDF</label>
                                    <PaymentFileUploadField
                                        className="mt-1"
                                        testIdPrefix="company-payments-company-cancelled-check-upload"
                                        aria-label="Upload cancelled check"
                                        disabled={companyPaymentInfoFieldsDisabled}
                                        isUploading={uploading === "company_cancelled_check"}
                                        chosenDisplayName={filePickerHint["company_cancelled_check"] ?? ""}
                                        onFilesSelected={(files) => {
                                            const key = "company_cancelled_check"
                                            const f = files?.[0]
                                            if (f) {
                                                setFilePickerHint((prev) => ({ ...prev, [key]: f.name }))
                                            }
                                            handleUploadFile("cancelled_check", files, undefined)
                                        }}
                                    />
                                    <p className="mt-2 text-xs text-slate-500">File uploads when you select it or click Upload.</p>
                                </div>
                                <div className="mt-auto space-y-2 border-t border-slate-200/80 pt-3">
                                    {normalizePaymentFiles(company?.["cancelled_check"]).map((f, i) => (
                                        <div key={f?.id ?? i} className="flex items-center gap-2">
                                            <button
                                                type="button"
                                                data-testid={`company-payments-company-cancelled-check-file-${String(f.id)}-download-button`}
                                                className={cn(PAY_LINK, "truncate text-left")}
                                                onClick={() => handleDownloadFile(String(f.id), FILE_TYPES.CANCEL_CHECK)}
                                                disabled={downloading === String(f.id)}
                                            >
                                                {downloading === String(f.id) ? "…" : f?.attachment_name ?? "Download"}
                                            </button>
                                            {!companyPaymentInfoFieldsDisabled && (
                                                <button
                                                    type="button"
                                                    data-testid={`company-payments-company-cancelled-check-file-${String(f.id)}-remove-button`}
                                                    onClick={() =>
                                                        setShowDeleteFileConfirm({
                                                            fileId: String(f.id),
                                                            type: "cancelled_check",
                                                            index: i,
                                                        })
                                                    }
                                                    className="shrink-0 rounded p-1 text-red-600 hover:bg-red-50"
                                                    aria-label="Remove file"
                                                >
                                                    <X size={14} />
                                                </button>
                                            )}
                                        </div>
                                    ))}
                                </div>
                            </div>
                        </div>
                    </div>

                    <p className="text-sm text-slate-500">
                        <span className="font-medium text-slate-600">Last update</span>{" "}
                        {String(company?.["payment_updated_by"] ?? "—")}{" "}
                        {formatPaymentUpdatedDate(company?.["payment_updated_date"])}
                    </p>
                </div>
            )}

            {/* Paper Check fields */}
            {companyForm.payment_method === "PAPER_CHECK" && (
                <div className="space-y-4 border-t border-slate-100 pt-5">
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                        <div>
                            <label className="block text-sm font-medium text-slate-600 mb-1">ATTN Payee</label>
                            <select
                                data-testid="company-payments-paper-attn-payee-select"
                                value={companyForm.payee_name}
                                onChange={(e) => updateCompanyForm({ payee_name: e.target.value })}
                                disabled={companyPaymentInfoFieldsDisabled}
                                className={cn(PAY_FIELD, "w-full")}
                            >
                                <option value="">Select ATTN Contact</option>
                                <option value="ACCOUNTING OFFICE">Accounting Office</option>
                                {companyStaff.map((s) => (
                                    <option key={s.user_id} value={String(s.user_id)}>
                                        {s.first_name} {s.last_name}
                                    </option>
                                ))}
                            </select>
                        </div>
                        <div className="md:col-span-2">
                            <label className="mb-1 block text-sm font-medium text-slate-600">
                                Street Address<span className="font-semibold text-[#dc2626]">*</span>
                            </label>
                            <textarea
                                data-testid="company-payments-paper-street-address-textarea"
                                rows={1}
                                placeholder="Street Address"
                                value={companyForm.payment_address}
                                onChange={(e) => {
                                    updateCompanyForm({ payment_address: e.target.value })
                                    clearPaymentAddrFieldError("payment_address", "company")
                                }}
                                disabled={companyPaymentInfoFieldsDisabled}
                                aria-invalid={
                                    paymentAddrValidateErrors?.scope === "company" &&
                                    !!paymentAddrValidateErrors.fields.payment_address
                                }
                                className={cn(
                                    PAY_FIELD,
                                    "min-h-[2.65rem] w-full resize-y",
                                    paymentAddrValidateErrors?.scope === "company" &&
                                        paymentAddrValidateErrors.fields.payment_address &&
                                        PAY_ADDR_INVALID_RING
                                )}
                            />
                            {paymentAddrValidateErrors?.scope === "company" &&
                            paymentAddrValidateErrors.fields.payment_address ? (
                                <PaymentAddrFieldError message="Street address is required" />
                            ) : null}
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-slate-600 mb-1">Suite / Mailstop</label>
                            <input
                                type="text"
                                data-testid="company-payments-paper-suite-mailstop-input"
                                placeholder="Suite / Mailstop"
                                value={companyForm.payment_shop}
                                onChange={(e) => updateCompanyForm({ payment_shop: e.target.value })}
                                disabled={companyPaymentInfoFieldsDisabled}
                                className={cn(PAY_FIELD, "w-full")}
                            />
                        </div>
                        <div>
                            <label className="mb-1 block text-sm font-medium text-slate-600">
                                City<span className="font-semibold text-[#dc2626]">*</span>
                            </label>
                            <input
                                type="text"
                                data-testid="company-payments-paper-city-input"
                                placeholder="City"
                                value={companyForm.payment_city}
                                onChange={(e) => {
                                    updateCompanyForm({ payment_city: e.target.value })
                                    clearPaymentAddrFieldError("payment_city", "company")
                                }}
                                disabled={companyPaymentInfoFieldsDisabled}
                                aria-invalid={
                                    paymentAddrValidateErrors?.scope === "company" &&
                                    !!paymentAddrValidateErrors.fields.payment_city
                                }
                                className={cn(
                                    PAY_FIELD,
                                    "w-full",
                                    paymentAddrValidateErrors?.scope === "company" &&
                                        paymentAddrValidateErrors.fields.payment_city &&
                                        PAY_ADDR_INVALID_RING
                                )}
                            />
                            {paymentAddrValidateErrors?.scope === "company" &&
                            paymentAddrValidateErrors.fields.payment_city ? (
                                <PaymentAddrFieldError message="City is required" />
                            ) : null}
                        </div>
                        <div>
                            <label className="mb-1 block text-sm font-medium text-slate-600">
                                State<span className="font-semibold text-[#dc2626]">*</span>
                            </label>
                            <select
                                data-testid="company-payments-paper-state-select"
                                value={companyForm.payment_state}
                                onChange={(e) => {
                                    updateCompanyForm({ payment_state: e.target.value })
                                    clearPaymentAddrFieldError("payment_state", "company")
                                }}
                                disabled={companyPaymentInfoFieldsDisabled}
                                aria-invalid={
                                    paymentAddrValidateErrors?.scope === "company" &&
                                    !!paymentAddrValidateErrors.fields.payment_state
                                }
                                className={cn(
                                    PAY_FIELD,
                                    "w-full",
                                    paymentAddrValidateErrors?.scope === "company" &&
                                        paymentAddrValidateErrors.fields.payment_state &&
                                        PAY_ADDR_INVALID_RING
                                )}
                            >
                                <option value="">Select State</option>
                                {states.map((s) => (
                                    <option key={s.id} value={s.state_code}>
                                        {s.state_name}
                                    </option>
                                ))}
                            </select>
                            {paymentAddrValidateErrors?.scope === "company" &&
                            paymentAddrValidateErrors.fields.payment_state ? (
                                <PaymentAddrFieldError message="State is required" />
                            ) : null}
                        </div>
                        <div>
                            <label className="mb-1 block text-sm font-medium text-slate-600">
                                Zipcode<span className="font-semibold text-[#dc2626]">*</span>
                            </label>
                            <input
                                type="text"
                                data-testid="company-payments-paper-zipcode-input"
                                placeholder="Zipcode"
                                value={companyForm.payment_zipcode}
                                onChange={(e) => {
                                    updateCompanyForm({ payment_zipcode: e.target.value })
                                    clearPaymentAddrFieldError("payment_zipcode", "company")
                                }}
                                disabled={companyPaymentInfoFieldsDisabled}
                                aria-invalid={
                                    paymentAddrValidateErrors?.scope === "company" &&
                                    !!paymentAddrValidateErrors.fields.payment_zipcode
                                }
                                className={cn(
                                    PAY_FIELD,
                                    "w-full",
                                    paymentAddrValidateErrors?.scope === "company" &&
                                        paymentAddrValidateErrors.fields.payment_zipcode &&
                                        PAY_ADDR_INVALID_RING
                                )}
                            />
                            {paymentAddrValidateErrors?.scope === "company" &&
                            paymentAddrValidateErrors.fields.payment_zipcode ? (
                                <PaymentAddrFieldError message="Zipcode is required" />
                            ) : null}
                        </div>
                    </div>
                    <div className="flex flex-wrap items-center gap-4">
                        <div className="text-sm text-slate-600">
                            Last Update By {String(company?.["payment_updated_by"] ?? "")}{" "}
                            {formatPaymentUpdatedDate(company?.["payment_updated_date"])}
                        </div>
                        {!companyPaymentInfoFieldsDisabled && (
                            <button
                                type="button"
                                data-testid="company-payments-paper-validate-address-button"
                                onClick={() => handleValidatePaymentAddress(undefined)}
                                className={PAY_BTN_PRIMARY}
                            >
                                Validate Address
                            </button>
                        )}
                    </div>
                    {/*
                      Angular company-edit: *ngIf="!['ACCOUNTING OFFICE'].includes(payee_name)" for Email / Verified / Edit User.
                      routerLink="/user-management/edit/{{payee_user_id}}" target="_blank" — React vs Angular via getUserManagementEditUrl.
                    */}
                    {!["ACCOUNTING OFFICE"].includes(String(companyForm.payee_name ?? "")) && (
                        <div className="mt-4 grid grid-cols-1 gap-4 border-t border-slate-100 pt-4 sm:grid-cols-[minmax(0,1fr)_minmax(0,1fr)_auto] sm:items-end">
                            <div className="min-w-0">
                                <p className="text-sm font-medium text-slate-600">Email Address:</p>
                                <p className="mt-1 break-all text-sm text-slate-800">
                                    {String(company?.["payee_email"] ?? "")}
                                </p>
                            </div>
                            <div className="min-w-0">
                                <p className="text-sm font-medium text-slate-600">Email Verified Status:</p>
                                <p className="mt-1 text-sm text-slate-800">
                                    {company?.["payee_email_verify"]
                                        ? String(company?.["payee_email_verify"])
                                        : "Unverified"}
                                </p>
                            </div>
                            <div className="flex sm:justify-end sm:pb-0.5">
                                {company?.["payee_user_id"] != null && String(company?.["payee_user_id"]) !== "" ? (
                                    <a
                                        data-testid="company-payments-paper-edit-payee-user-link"
                                        href={getUserManagementEditUrl(String(company?.["payee_user_id"]))}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className={cn(PAY_BTN_PRIMARY, "inline-flex no-underline")}
                                    >
                                        Edit User
                                    </a>
                                ) : null}
                            </div>
                        </div>
                    )}
                </div>
            )}

            </SectionCard>

                    <button
                type="submit"
                data-testid="company-payments-hidden-submit"
                tabIndex={-1}
                aria-hidden
                style={{ position: "absolute", width: 0, height: 0, opacity: 0, pointerEvents: "none" }}
            />
            </form>
    )
}
